import { setCookie } from "@insite/client-framework/Common/Cookies";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import LazyImage from "@insite/mobius/LazyImage";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setPLPRendering from "@ucs/Handlers/SetPLPRendering";
import setBreadcrumbFilterUrl from "@ucs/Store/Custom/SetBreadcrumbFilterUrl";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    settingsCollection: getSettingsCollection(state),
    productCompareChecked: getProductCompareChecked(state, props.productContext),
    isProductCompareFull: getIsProductCompareFull(state),
});

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
    setPLPRendering,
    setBreadcrumbFilterUrl,
};

interface OwnProps {
    view: ProductListViewType | undefined,
    extendedStyles?: InjectableCss,
    hideCompare?: boolean,
}

type Props = ReturnType<typeof mapStateToProps> & HasProductContext & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;


export const productGridStyles = {
    logo: {
        css: css`
            font-size: 18px;
            font-weight: 500;
            color: black;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 16px;
                    `,
                    css`
                        font-size: 16px;
                    `,
                    css`
                        font-size: 16px;
                    `,
                ]);
            }}
        `,
    },
    optionsText: {
        css: css`
            font-size: 12px;
            font-weight: 700;
            color: ${UCSColors.gray04};
            margin-bottom: 8px;
        `,
    },
    optionsTextPrimary: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    optionsItemText: {
        css: css`
            white-space: normal;
            font-size: 14px;
            line-height: 16px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                        line-height: 14px;
                        margin-bottom: 4px;
                    `,
                    css`
                        font-size: 12px;
                        line-height: 14px;
                        margin-bottom: 4px;
                    `,
                ]);
            }}
        `,
    },
    brand: {
        css: css`
            color: ${UCSColors.gray04};
            font-size: 16px;
            font-weight: 500;
            display: block;
            text-transform: uppercase;
        `,
    },
};

const ProductGridItemWrapper = styled('div') <InjectableCss<any> & { view: string | undefined }>`
    display: flex;
    max-width: calc(25% - 30px);
    margin: 15px 15px;
    flex: 1 1;
    position: relative;
    flex-basis: calc(50% - 15px);
    align-self: stretch;
    flex-direction: column;
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.15);
    ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                max-width: calc(50% - 10px);
                margin: 5px;
            `,
            css`
                max-width: calc(33.33% - 10px);
                margin: 5px;
            `,
            css`
                max-width: calc(50% - 20px);
                margin: 10px;
            `,
            css` 
                max-width: calc(33.33% - 30px);
            `,
            css`
                max-width: calc(33.33% - 30px);
            `,
        ]);
    }}
    @media only screen and (min-width: 990px) and (max-width: 1290px) {
        max-width: calc(50% - 30px);
    }

    @media only screen and (min-width: 1500px) {
        max-width: calc(25% - 30px);
    }
    ${({ view, theme }) => {
        if (view === "List") {
            const _css = css`
                flex-basis: 100% !important;
                width: 100% !important;
                max-width: none !important;
                flex-direction: row;
                margin: 0px 0px 41px 0px;
                padding-right: 10px;
                background-color: white;
            `;

            return breakpointMediaQueries(theme, [
                null, null, null, _css, _css
            ]);
        }
    }};
    ${injectCss}
`;

const ImageWrapper = styled('a') <{ view: string | undefined, bgImage: string }>`
    background-position: center center;
    background-size: cover;
    width: 100%;
    position: relative;
    background-image: url('${p => p.bgImage}');
    border-bottom: 1px solid ${UCSColors.gray07};
    /* height: 100%; */
    &:before {
        content: "";
        display: block;
        padding-top: 100%;
        ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                    padding-top: 65%;
                `,
            css`
                    padding-top: 65%;
                `,
            css`
                    padding-top: 65%;
                `,
        ]);
    }}
    }

    ${({ view, theme }) => {
        if (view === "List") {
            const _css = css`
                display: block;
                border-bottom: none;
                border-right:  1px solid ${UCSColors.gray07};
                height: 100%;
                &:before {
                    padding-top: 90% !important;
                }
            `;

            return breakpointMediaQueries(theme, [
                null, null, null, _css, _css
            ]);
        }
    }};
`;

const Compare = styled('div') <InjectableCss<any>>`
    background-color: ${UCSColors.gray10};
    padding: 8px 20px;
    height: 40px;
    line-height: 30px;
    vertical-align: middle;
    border-top: 1px solid ${UCSColors.gray07};
    position: relative;
    label {
        color: ${UCSColors.gray04};
        font-size: 14px;   
        font-weighT: 500;
        letter-spacing: 1.2px;
        text-transform: uppercase;
    }
    ${injectCss}
`;

const ContentArea = styled.a`
    padding: 15px 20px;
    background-color: white;
    flex: 1 1;
    color: ${UCSColors.gray03};
`;

const ItemDetails = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 8px;
`;

const OptionsContainer = styled('div') <{ removeMargins: boolean, view: string | undefined }>`
    display: flex;
    flex-direction: column;
    /* width: calc(100% - 100px); */
    flex-basis: auto;
    flex-grow: 1;
    ${p => p.removeMargins ? `margin-right: 0px;` : `margin-right: 10px`};
    border-top: 1px solid ${UCSColors.gray07};
    padding-top: 8px;
    overflow: hidden;
    ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                    width: calc(100% - 25px);
                `,
            css`
                    width: calc(100% - 25px);
                `,
            css`
                    width: calc(100% - 25px);
                `,
        ]);
    }}
    ul {
        list-style: disc;
        list-style-position: inside;
        ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                    list-style: none;
                `,
            css`
                    list-style: none;
                `,
            css`
                    list-style: none;
                `,
        ]);
    }}
        li {
            font-size: 14px;
            text-overflow: ellipsis;
            overflow: hidden;
            color: ${UCSColors.gray03};
            white-space: nowrap;
            ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                        white-space: normal;
                    `,
            css`
                        white-space: normal;
                    `,
            css`
                        white-space: normal;
                    `,
        ]);
    }}
        }
    }

    ${({ view, theme }) => {
        if (view === "List") {
            const _css = css`
                flex-basis: 50%;
                margin-top: 15px;
                padding-bottom: 15px;
            `;

            return breakpointMediaQueries(theme, [
                null, null, null, _css, _css
            ]);
        }
    }};
`;

const ComplianceContainer = styled('div') <{ view: string | undefined }>`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    flex-shrink: 0;
    flex-basis: auto;
    max-width: 90px;
    /* width: 90px;
    min-width: 90px; */
    ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                    max-width: 25px;
                    flex-direction: column;
                `,
            css`
                    max-width: 25px;
                    flex-direction: column;
                `,
            css`
                    max-width: 25px;
                    flex-direction: column;
                `,
        ]);
    }}
    > div {
        width: 40px;
        height: 50px;
        ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                    width: 25px;
                    height: 25px;
                `,
            css`
                    width: 25px;
                    height: 25px;

                `,
            css`
                    width: 25px;
                    height: 25px;

                `,
        ]);
    }}
        border-top: 1px solid ${UCSColors.gray07};

        /* padding: 5px; */
        &:nth-child(odd) {
            margin-right: 5px;
            ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                        margin-right: 0px;
                    `,
            css`
                        margin-right: 0px;
                    `,
            css`
                        margin-right: 0px;
                    `,
        ]);
    }}
        }
        &:nth-child(even) {
            margin-left: 5px;
            ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                    margin-left: 0px;
                `,
            css`
                    margin-left: 0px;
                `,
            css`
                    margin-left: 0px;
                `,
        ]);
    }}
        }
    }

    ${({ view, theme }) => {
        if (view === "List") {
            const _css = css`
                margin-top: 15px;
                padding-bottom: 15px;
                max-height: 190px;
                flex-direction: column-reverse;
                display: inline;
            `;

            return breakpointMediaQueries(theme, [
                null, null, null, _css, _css
            ]);
        }
    }};
`;

const ComplianceItem = styled.div`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    img {
        max-width: 40px;
    }
`;

const Tag = styled('div') <{ view: string | undefined }>`
    position: absolute;
    top: 0px;
    right: 0px;
    background-color: ${UCSColors.gray03};
    padding: 8px 16px;
    color: white;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    ${({ view, theme }) => {
        if (view === "List") {
            const _css = css`
                padding: 4px 8px;
            `;

            return breakpointMediaQueries(theme, [
                null, null, null, _css, _css
            ]);
        }
    }};
`;

const ListLeftContentArea = styled.div`
    width: 165px;
    min-width: 165px;
`;

const ListCenterContentArea = styled.div`
    margin-left: 20px;
    margin-right: 40px;
    min-width: 35%;
    width: 35%;
    padding: 10px 0px;
`;

const ProductGridItem: React.FC<Props> = ({
    view,
    extendedStyles,
    hideCompare,
    productContext,
    isProductCompareFull,
    settingsCollection,
    productCompareChecked,
    addProductToCompare,
    removeProductFromCompare,
    history,
    setBreadcrumbFilterUrl,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const product = productContext.product;
    const brand = product.brand;

    const imagePath = product.smallImagePath || product.mediumImagePath;
    const compliances: string[] = [];
    const skuCount: string = product.properties.totalSKUs || "??";
    let tag = "";
    if (product.attributeTypes) {
        product.attributeTypes.forEach(attribute => {
            if (attribute.name.toLowerCase().includes("complianceandcertification")) {
                attribute.attributeValues?.forEach(attributeValue => {
                    compliances.push(attributeValue.value);
                });
            }
            if (attribute.name.toLowerCase() === "producttiletag" && attribute.attributeValues) {
                tag = attribute.attributeValues[0].value;
            }
        });
    }

    const pdpPathHandler = (productPath: string) => {
        setBreadcrumbFilterUrl({ breadcrumbFilterUrl: { productTitle: productPath, searchFilters: window.location.search } })
        const gridElement = document!.getElementById("productGridWrapper");
        if (gridElement) {
            const y = gridElement.getBoundingClientRect().height;
            sessionStorage.setItem("plpGridHeight", y.toString());
        }
        history.push(productPath);
    };

    compliances.sort();

    const traits: string[] = [];
    if (product.variantTraits) {
        product.variantTraits.forEach((variantTrait) => {
            traits.push(variantTrait.nameDisplay);
        });
    } else {
        product?.attributeTypes?.forEach(attribute => {
            if (attribute.name.toLowerCase().includes("varianttraits")) {
                attribute.attributeValues?.forEach(attributeValue => {
                    traits.push(attributeValue.value);
                });
            }
        });
    }

    const productDetailPath = productContext.productInfo.productDetailPath || product.canonicalUrl;

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: product.id });
        } else {
            removeProductFromCompare({ productId: product.id });
        }
    };

    if (view === "Grid" || !view) {
        return (
            <ProductGridItemWrapper view={view} css={extendedStyles?.css}>
                <ImageWrapper onClick={() => pdpPathHandler(productDetailPath)} view={view} bgImage={imagePath}>
                    {tag && (
                        <Tag view={view}>{tag}</Tag>
                    )}
                </ImageWrapper>

                {!hideCompare &&
                    <Compare css={css`                    
                        :after {
                            content: "";
                            position: absolute;
                            left: 20px;
                            bottom: 0;
                            height: 1px;
                            width: calc(100% - 40px);
                            border-bottom: 1px solid ${UCSColors.gray07};
                        }`}>
                        <Checkbox css={css`width: 22px; height: 22px;`} checked={productCompareChecked} onChange={handleChanged}>
                            {translate("Compare")}
                        </Checkbox>
                    </Compare>
                }
                <ContentArea onClick={() => pdpPathHandler(productDetailPath)}>
                    {brand && (
                        <Typography {...productGridStyles.brand}>
                            {brand.name}
                        </Typography>
                    )}

                    <Typography {...productGridStyles.logo}>
                        {product.productTitle}
                    </Typography>
                    <ItemDetails>
                        <OptionsContainer removeMargins={compliances.length === 0} view={view}>
                            {(skuCount === "1") ? (
                                <Typography {...productGridStyles.optionsText}>
                                    <Typography {...productGridStyles.optionsTextPrimary}>{skuCount} </Typography>
                                    {translate("Product. Configure:")}
                                </Typography>
                            ) :
                                <Typography {...productGridStyles.optionsText}>
                                    <Typography {...productGridStyles.optionsTextPrimary}>{skuCount} </Typography>
                                    {translate("Products. Configure:")}
                                </Typography>
                            }
                            <ul>
                                {traits.map((trait, i) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <li key={i}>
                                        <Typography {...productGridStyles.optionsItemText}>
                                            {trait}
                                        </Typography>
                                    </li>
                                ))}
                            </ul>
                        </OptionsContainer>
                        <ComplianceContainer view={view}>
                            {compliances.map((compliance, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <ComplianceItem key={i}>
                                    <LazyImage src={`/UserFiles/compliance/${compliance}_tile.svg`}></LazyImage>
                                </ComplianceItem>
                            ))}
                        </ComplianceContainer>
                    </ItemDetails>
                </ContentArea>
            </ProductGridItemWrapper>
        );
    } if (view === "List") {
        return (
            <ProductGridItemWrapper view={view} css={extendedStyles?.css}>
                <ListLeftContentArea>
                    <ImageWrapper onClick={() => pdpPathHandler(productDetailPath)} view={view} bgImage={imagePath}>
                        {tag && (
                            <Tag view={view}>{tag}</Tag>
                        )}
                    </ImageWrapper>

                    {!hideCompare &&
                        <Compare>
                            <Checkbox css={css`width: 22px; height: 22px;`} checked={productCompareChecked} onChange={handleChanged}>
                                {translate("Compare")}
                            </Checkbox>
                        </Compare>
                    }

                </ListLeftContentArea>
                <ListCenterContentArea>
                    {brand && (
                        <Typography {...productGridStyles.brand}>
                            {brand.name}
                        </Typography>
                    )}

                    <Typography {...productGridStyles.logo}>
                        {product.productTitle}
                    </Typography>
                </ListCenterContentArea>
                <OptionsContainer removeMargins={compliances.length === 0} view={view}>
                    {(skuCount === "1") ? (
                        <Typography {...productGridStyles.optionsText}>
                            <Typography {...productGridStyles.optionsTextPrimary}>{skuCount}</Typography> {translate("Product. Configure:")}
                        </Typography>
                    ) :
                        <Typography {...productGridStyles.optionsText}>
                            <Typography {...productGridStyles.optionsTextPrimary}>{skuCount}</Typography> {translate("Products. Configure:")}
                        </Typography>
                    }
                    <ul>
                        {traits.map((trait, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={i}>
                                <Typography {...productGridStyles.optionsItemText}>
                                    {trait}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                </OptionsContainer>
                <ComplianceContainer view={view}>
                    {compliances.map((compliance, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <ComplianceItem key={i}>
                            <LazyImage src={`/UserFiles/compliance/${compliance}_tile.svg`}></LazyImage>
                        </ComplianceItem>
                    ))}
                </ComplianceContainer>
            </ProductGridItemWrapper>
        );
    }
    return <div></div>

};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductGridItem)));